// Kei Hauler: a tiny cab-forward truck with a short open ribbed bed. Numbers in hd px (docs/CAR_ART.md). Built with `custom`
// because the bed is open. Cab at the front (u 3..18), bed behind (u -18..3). The old art's gunner variant is not
// expressible here: the engine passes no variant to a model.
import { K, dk, lt } from '../hd-kit';
import { registerCarModel, type CarModel, type V3 } from '../car-drawn';

const HW = 10, HWC = 9.2, ZB = 8, ZW = 12.2, ZR = 19.5, UC = 3, UF = 18, UR = -18;

export const kei: CarModel = {
  body: [[-18, 3], [18, 3], [18, 8], [-18, 8]], hw: HW, axles: [11.5, -10.5], wheelR: 4,
  custom(P) {
    const { fill, endRect, sideRect, body } = P, lamp = P.lampOn;
    const seam = dk(body, .8);
    const side = (s: number) => {
      const v = s * HW, n: V3 = [0, s, 0];
      // lower body and bed wall as one outer face with the wheel arches cut in
      const arches = () => { for (const u of P.axles) fill(P.disc(u, s * (HW + P.flare), 4, 4.9).map(p => [p[0], p[1], Math.min(7.8, Math.max(3, p[2]))] as V3), K.ink); };
      P.face([[UR, v, 3], [UF, v, 3], [UF, v, ZB], [UC, v, ZB], [UC, v, ZW], [UR, v, ZW]], n, P.tone(n), () => {
        fill(sideRect(v, UR, UF, 3, 4.2), K.metalLo); // sill
        arches();
        for (let u = -16; u < UC - 1; u += 3) fill(sideRect(v, u, u + .8, 8.6, 11.8), dk(body, .3)); // bed wall ribs
        fill(sideRect(v, UR, UC, 11.6, 12.2), lt(body, .35)); // wall rim highlight
        if (P.wrecked) { fill([[-14, v, 12.2], [-9, v, 9.6], [-5, v, 12.2]], dk(body, .6)); fill([[-14, v, 12.2], [-12.6, v, 11.4], [-9, v, 9.6], [-9, v, 10.4]], lt(body, .4)); } // the bed wall is caved in, with a lit fold
        fill(sideRect(v, 7.5, 8.3, 4.4, 7.4), seam); // cab/bed seam
        fill(sideRect(v, UF - 2, UF - .4, 5, 7), P.wrecked ? K.metalLo : K.amber); // side marker
      });
      // inner face of the bed wall (seen from the opposite side)
      const iv = s * (HW - 1), nin: V3 = [0, -s, 0];
      P.face([[UR + 1, iv, ZB], [UC, iv, ZB], [UC, iv, ZW], [UR + 1, iv, ZW]], nin, dk(P.tone(nin), .15), () => {
        for (let u = -16; u < UC - 1; u += 3) fill(sideRect(iv, u, u + .8, 8.4, 11.6), dk(body, .5));
      });
      // wall top
      P.face([[UR, v, ZW], [UC, v, ZW], [UC, iv, ZW], [UR, iv, ZW]], [0, 0, 1], lt(body, .3));
      // cab greenhouse side with the window
      const cv = s * HWC, ncs: V3 = [0, s, 0];
      P.face([[UC, cv, ZB], [UF, cv, ZB], [UF, cv, 13], [14, cv, ZR], [UC, cv, ZR]], ncs, P.tone(ncs), () => {
        const gt = P.glassTone(ncs);
        const win: V3[] = [[5.2, cv, 13], [5.2, cv, 18.2], [12.6, cv, 18.2], [16.2, cv, 13]];
        fill(win, gt); P.cracks(win);
        if (!P.wrecked) fill([[5.2, cv, 13], [5.2, cv, 18.2], [6.2, cv, 18.2], [6.2, cv, 13]], K.glassHi);
        fill(sideRect(cv, 9, 9.7, 13, 18.2), seam); // B pillar
        fill(sideRect(cv, 9, 12, 10.3, 11), K.metalHi); // door handle
        fill(sideRect(cv, 9.5, 9.6, 8, 13), seam);
      }, true);
    };
    // chassis ends
    P.face(endRect(UR, -HW, HW, 3, ZB), [-1, 0, 0], P.tone([-1, 0, 0]), undefined);
    P.face(endRect(UF, -HW, HW, 3, ZB), [1, 0, 0], P.tone([1, 0, 0]), () => { // front bumper
      fill(endRect(UF, -HW, HW, 3, 5.2), K.metal); fill(endRect(UF, -HW, HW, 3, 4), K.metalLo);
      fill(endRect(UF, -2.5, 2.5, 5.6, 7.4), K.white); fill(endRect(UF, -1.8, 1.8, 6, 7), K.metalLo); // plate
    });
    // floor with ribs
    const FL = ZB + .1;
    P.face([[UR + 1, -HW + 1, FL], [UC, -HW + 1, FL], [UC, HW - 1, FL], [UR + 1, HW - 1, FL]], [0, 0, 1], K.metalLo, () => {
      for (let u = UR + 2.5; u < UC - 1; u += 2.5) fill([[u, -HW + 1, FL], [u + .9, -HW + 1, FL], [u + .9, HW - 1, FL], [u, HW - 1, FL]], K.metal);
      if (P.gunner && !P.wrecked) { // gunner variant: an empty swivel mount instead of the crate (the player standing there is a character)
        fill([[-12, -4.6, FL], [-4, -4.6, FL], [-4, 4.6, FL], [-12, 4.6, FL]], dk(K.cyan, .35)); fill(P.disc(-8, 0, FL, 4.2, 14), K.ink); fill(P.disc(-8, 0, FL, 3.3, 14), K.metalLo); fill(P.disc(-8, 0, FL, 2.4, 12), K.metal); // pad and ring
        for (const a of [0, 1, 2, 3]) { const u = -8 + Math.cos(a * Math.PI / 2) * 4.4, v = Math.sin(a * Math.PI / 2) * 4.4; fill(P.disc(u, v, FL, .7, 6), K.metalHi); } // bolts
        fill(sideRect(-1, -9.8, -6.2, FL, FL + 7), K.metalHi); fill(sideRect(1, -9.8, -6.2, FL, FL + 7), K.metal); fill(endRect(-6.2, -1, 1, FL, FL + 7), K.metal); fill(endRect(-9.8, -1, 1, FL, FL + 7), K.metalLo); // thick post
        fill([[-9.8, -1.4, FL + 7], [-6.2, -1.4, FL + 7], [-6.2, 1.4, FL + 7], [-9.8, 1.4, FL + 7]], lt(K.metalHi, .3)); fill(P.disc(-8, 0, FL + 7.1, 1, 8), K.ink); // lit top cap
        return;
      }
      if (P.wrecked) { // the crate burst: loose planks across the bed, a scorched patch
        fill([[-15, -6, FL], [-9, -3, FL], [-10, -1.4, FL], [-16, -4.4, FL]], K.wood); fill([[-7, 1, FL], [-2, 4, FL], [-3, 5.4, FL], [-8, 2.4, FL]], K.woodHi); fill([[-12, 3, FL], [-11, 8, FL], [-12.6, 8, FL], [-13.6, 3, FL]], K.woodLo);
        fill([[-6, -7, FL], [0, -6, FL], [-1, -2, FL], [-5, -3, FL]], K.ink); return;
      }
      // tied wooden crate with a rope
      const c0 = -12, c1 = -4, cz = FL + 4;
      fill([[c0, -5, FL], [c1, -5, FL], [c1, 5, FL], [c0, 5, FL]], K.ink);
      fill([[c0, -5, FL], [c0, -5, cz], [c0, 5, cz], [c0, 5, FL]], K.wood);
      fill([[c0, -5, cz], [c1, -5, cz], [c1, 5, cz], [c0, 5, cz]], K.woodHi);
      fill([[c0 + 3.5, -5, cz + .01], [c0 + 4.5, -5, cz + .01], [c0 + 4.5, 5, cz + .01], [c0 + 3.5, 5, cz + .01]], K.warmLo);
      fill(sideRect(5, c0, c1, FL, cz), K.wood); fill(sideRect(-5, c0, c1, FL, cz), K.woodHi);
      fill(sideRect(5, c0 + 3.5, c0 + 4.5, FL, cz), K.warmLo);
    });
    // tailgate (outer and inner), tail lamps
    const ng: V3 = [-1, 0, 0];
    if (P.wrecked) { // the tailgate hangs open on one bent hinge: it drops below the bed floor, the far corner lower than the near one
      const ho: V3 = [-.55, 0, .83];
      P.face([[UR, -HW, ZB], [UR, HW, ZB], [UR - 3.4, HW, ZB - 4.6], [UR - 3.8, -HW, ZB - 2.6]], ho, dk(P.tone(ho), .2), () => {
        for (const vv of [-6, -1, 4]) fill([[UR - .4, vv, ZB - .3], [UR - .4, vv + 1, ZB - .3], [UR - 3.4, vv + 1, ZB - 3.6 - vv * .1], [UR - 3.4, vv, ZB - 3.6 - vv * .1]], dk(body, .5)); // ribs on its inside
      });
    } else P.face(endRect(UR, -HW, HW, ZB, ZW), ng, P.tone(ng), () => {
      for (const s of [1, -1]) { fill(endRect(UR, s * 6, s * 9.4, 8.6, 10.4), K.ink); fill(endRect(UR, s * 6.4, s * 9, 8.9, 10.1), lamp ? K.red : K.metalLo); if (lamp) fill(endRect(UR, s * 6.8, s * 8, 9.2, 9.8), K.pink); }
      fill(endRect(UR, -3, 3, 8.8, 10.6), K.white); fill(endRect(UR, -2.2, 2.2, 9.2, 10.2), K.metalLo);
      fill(endRect(UR, -HW, HW, 11.6, ZW), lt(body, .35));
    });
    if (!P.wrecked) P.face(endRect(UR + 1, -HW + 1, HW - 1, ZB, ZW), [1, 0, 0], dk(P.tone([1, 0, 0]), .15));
    if (!P.wrecked) P.face([[UR, -HW, ZW], [UR + 1, -HW, ZW], [UR + 1, HW, ZW], [UR, HW, ZW]], [0, 0, 1], lt(body, .3));
    side(1); side(-1);
    // cab: rear wall (faces the bed), roof, windscreen and nose
    const nr: V3 = [-1, 0, 0];
    P.face(endRect(UC, -HWC, HWC, ZB, ZR), nr, P.tone(nr), () => {
      const pane = endRect(UC, -6.4, 6.4, 13, 18); fill(pane, P.glassTone(nr)); P.cracks(pane);
      fill(endRect(UC, -HWC, HWC, ZB, 9.4), dk(body, .3));
    }, true);
    P.face([[UC, -HWC, ZR], [14, -HWC, ZR], [14, HWC, ZR], [UC, HWC, ZR]], [0, 0, 1], lt(body, .3), () => {
      fill([[UC, -HWC + .5, ZR + .01], [14, -HWC + .5, ZR + .01], [14, -HWC + 1.5, ZR + .01], [UC, -HWC + 1.5, ZR + .01]], lt(body, .55));
      fill([[UC, -HWC, ZR + .01], [UC + 1.1, -HWC, ZR + .01], [UC + 1.1, HWC, ZR + .01], [UC, HWC, ZR + .01]], dk(body, .22));
    }, true);
    const ws: V3 = [6.5 / 7.6, 0, 4 / 7.6]; // windscreen leans back
    const wat = (t: number, v: number): V3 => [UF + (14 - UF) * t, v, 13 + (ZR - 13) * t];
    P.face([[UF, -HWC, 13], [14, -HWC, ZR], [14, HWC, ZR], [UF, HWC, 13]], ws, P.tone(ws), () => {
      if (P.toward(ws) < .6) return;
      const pane = [wat(.1, -HWC + .9), wat(.9, -HWC + .9), wat(.9, HWC - .9), wat(.1, HWC - .9)]; fill(pane, P.glassTone(ws));
      if (!P.wrecked) fill([wat(.1, -HWC + .9), wat(.9, -HWC + .9), wat(.9, -HWC + 1.9), wat(.1, -HWC + 1.9)], K.glassHi);
      fill([wat(.1, -.4), wat(.9, -.4), wat(.9, .4), wat(.1, .4)], dk(body, .3)); // centre post
      P.cracks(pane);
    }, true);
    const nf: V3 = [1, 0, 0];
    P.face(endRect(UF, -HWC, HWC, ZB, 13), nf, P.tone(nf), () => { // flat nose: grille and lamps
      fill(endRect(UF, -3.6, 3.6, 8.8, 11.6), K.ink); for (let v = -3; v < 3.5; v += 1.5) fill(endRect(UF, v, v + .7, 9.1, 11.3), K.metalLo);
      for (const s of [1, -1]) { fill(endRect(UF, s * 4.6, s * 8.6, 9, 11.8), K.ink); fill(endRect(UF, s * 5, s * 8.2, 9.3, 11.5), lamp ? K.warm : K.metalLo); if (lamp) fill(endRect(UF, s * 5.4, s * 7.8, 10, 11.2), K.warmHi); }
    }, true);
  },
};
registerCarModel('kei', kei);
